import { PaginatedRequest, PaginatedResponse } from '@/types';
import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { API_BUSINESS_URL } from '@/utils';
import {
  PickupAuditApi,
  PickupAuditFilters,
  InitiatePickupAuditExportResponse,
  PickupAuditExportJobStatus,
  PickupTransitionOption,
} from '../models';

export type GetPickupAuditsQueryArgs = {
  filters: PickupAuditFilters;
  paginated: PaginatedRequest;
};

const pickupAuditService = createApi({
  reducerPath: 'pickupAuditService',
  baseQuery: createBaseQueryWithReauth(`${API_BUSINESS_URL}/pickup-audits`),
  tagTypes: ['PickupAudits', 'PickupTransitions'],
  endpoints: (builder) => ({
    getPickupAudits: builder.query<
      PaginatedResponse<PickupAuditApi>,
      GetPickupAuditsQueryArgs
    >({
      query: ({ paginated, filters }) => {
        const {
          startDate,
          endDate,
          shippingNumber,
          locationId,
          transitionId,
          logisticOperatorId,
        } = filters;

        const params: Record<string, string | number> = {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
        };

        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
        if (shippingNumber !== undefined && shippingNumber !== '') {
          params.shippingNumber = shippingNumber;
        }
        if (locationId && locationId.trim() !== '') {
          params.locationId = locationId.trim();
        }
        if (transitionId && transitionId.trim() !== '') {
          params.transitionId = transitionId.trim();
        }
        if (logisticOperatorId && logisticOperatorId.trim() !== '') {
          params.logisticOperatorId = logisticOperatorId.trim();
        }

        return {
          url: '',
          params,
        };
      },
      providesTags: ['PickupAudits'],
    }),

    getPickupTransitions: builder.query<PickupTransitionOption[], void>({
      query: () => ({
        url: '/transitions',
      }),
      providesTags: ['PickupTransitions'],
    }),

    initiatePickupAuditExport: builder.mutation<
      InitiatePickupAuditExportResponse,
      PickupAuditFilters
    >({
      query: (body) => ({
        url: '/export',
        method: 'POST',
        body,
      }),
    }),

    getPickupAuditExportJobStatus: builder.query<
      PickupAuditExportJobStatus,
      string
    >({
      query: (jobId) => ({
        url: `/export/jobs/${jobId}`,
      }),
    }),
  }),
});

export default pickupAuditService;

export const {
  useGetPickupAuditsQuery,
  useGetPickupTransitionsQuery,
  useInitiatePickupAuditExportMutation,
  useLazyGetPickupAuditExportJobStatusQuery,
} = pickupAuditService;
